import * as React from 'react';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { tokens } from '@hotjar/hotsauce';
import { ThumbToggleButton } from './ThumbToggleButton';
import { ThumbsUpIcon } from './ThumbsUpIcon';
import { ThumbsDownIcon } from './ThumbsDownIcon';

export type AnswerType = boolean | null;

interface ThumbsUpDownTooltipContentProps {
  questionLabel: React.ReactElement | string;
  answer: AnswerType;
  onAnswerChange: (answer: AnswerType) => void;
  footer?: React.ReactChild | React.ReactChild[];
  isLoading?: boolean;
  thumbsUpLabel: string;
  thumbsDownLabel: string;
}

const WrapperDiv = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const BodyDiv = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: ${tokens.spacer.x2} ${tokens.spacer.x4};
  font-weight: 400;
  font-size: 12px;
`;

const ButtonSectionDiv = styled.div`
  display: flex;
  & > button {
    /* Will put 8px gap between each buttons */
    margin: 0 ${tokens.spacer.x1};
  }
`;

const QuestionDiv = styled.div`
  font-weight: 400;
  padding-bottom: 12px;
  font-size: 14px;
`;

const FooterDiv = styled.div`
  width: 100%;
  color: ${tokens.color.text_light_inverse};
  background-color: rgba(255, 255, 255, 0.1);
  padding: ${tokens.spacer.x1} ${tokens.spacer.x2};
  font-weight: 400;
  font-size: 12px;
`;

export const ThumbsUpDownTooltipContent = ({
  questionLabel,
  answer,
  onAnswerChange,
  footer,
  isLoading,
  thumbsUpLabel,
  thumbsDownLabel,
}: ThumbsUpDownTooltipContentProps): JSX.Element => {
  const { t } = useTranslation();

  const handleYesClick = (e: React.SyntheticEvent<HTMLButtonElement, MouseEvent>) => {
    e.stopPropagation();

    if (answer === true) {
      onAnswerChange(null);
    } else {
      onAnswerChange(true);
    }
  };

  const handleNoClick = (e: React.SyntheticEvent<HTMLButtonElement, MouseEvent>) => {
    e.stopPropagation();

    if (answer === false) {
      onAnswerChange(null);
    } else {
      onAnswerChange(false);
    }
  };

  return (
    <WrapperDiv>
      <BodyDiv>
        <QuestionDiv>{questionLabel}</QuestionDiv>
        <ButtonSectionDiv>
          <ThumbToggleButton
            isToggled={answer === true}
            onClick={handleYesClick}
            disabled={isLoading}
            ariaLabel={thumbsUpLabel}
          >
            {t('common:components.thumbs-up-down.yes')}
            &nbsp;&nbsp;
            <ThumbsUpIcon size={13} />
          </ThumbToggleButton>
          <ThumbToggleButton
            isToggled={answer === false}
            onClick={handleNoClick}
            disabled={isLoading}
            ariaLabel={thumbsDownLabel}
          >
            {t('common:components.thumbs-up-down.no')}
            &nbsp;&nbsp;
            <ThumbsDownIcon size={13} />
          </ThumbToggleButton>
        </ButtonSectionDiv>
      </BodyDiv>
      {footer && <FooterDiv>{footer}</FooterDiv>}
    </WrapperDiv>
  );
};
